type DialogConfig = {
    id: string,
    title: string,
    icon?: 'warning' | 'error' | 'success' | 'info' | 'question',
    message?: string,
    htmlBody?: string,
    focusButtons?: boolean,
    onDialogOpen?: Function,
    centerButtons?: boolean,
    centerHeader?: boolean,
    btnConfigs: ButtonConfig[],
    customClasses?: CustomClasses,
};

type CustomClasses = {
    container?: string | string[],
    htmlBody?: string | string[],
    message?: string | string[],
    title?: string | string[],
};

type ButtonConfig = {
    text: string,
    condition?: Function,
    preClickEvent?: Function,
    preClickValidation?: PreClickValidation | PreClickValidation[],
    onClickEvent?: Function
    closeDialog?: boolean;
    colour?: 'Primary' | 'Danger' | 'Secondary'
}

type PreClickValidation = {
    fieldId: string,
    errorId?: string,
    predicate: Function,
    errorMsg: string,
}

function showDialog(dialogConfig: DialogConfig) {
    // First remove stale dialog boxes
    document.getElementById(`${dialogConfig.id}-dialog`)?.remove();
    const dialog = document.createElement('dialog');

    function addCustomClasses(element: HTMLElement, customClasses: string | string[] | undefined) {
        if (!customClasses || customClasses.length === 0) return;
        const addClasses = Array.isArray(customClasses) ? customClasses : [customClasses];
        element.classList.add(...addClasses);
    }

    // Build the container
    dialog.classList.add('dialog-container', 'aurion-dialog', 'shadow');
    if (dialogConfig.id === 'time-out') dialog.classList.add('dialog-container', 'p-4', 'p-lg-5');
    else dialog.classList.add('p-2', 'p-md-3');
    dialog.setAttribute('aria-live', 'polite'); // https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/ARIA_Live_Regions
    addCustomClasses(dialog, dialogConfig.customClasses?.container);
    dialog.id = `${dialogConfig.id}-dialog`;

    // Add title
    if (dialogConfig.title) {
        const dialogTitle = document.createElement('h2');
        dialogTitle.textContent = dialogConfig.title;
        if (dialogConfig.centerHeader) dialogTitle.classList.add('dialog-title', 'text-center', 'd-block');
        else dialogTitle.classList.add('dialog-title', 'text-start', 'd-block');
        addCustomClasses(dialogTitle, dialogConfig.customClasses?.title);
        dialog.appendChild(dialogTitle);
    }

    if (dialogConfig.icon) addIcon(dialog, dialogConfig.icon);

    // Add description
    if (dialogConfig.message) {
        const dialogMessage = document.createElement('span');
        dialogMessage.textContent = dialogConfig.message;
        dialogMessage.classList.add('dialog-message', 'd-block');
        addCustomClasses(dialogMessage, dialogConfig.customClasses?.message);
        dialog.appendChild(dialogMessage);
    }

    // Allow for additional html elements (in ERM)
    if (dialogConfig.htmlBody) {
        const htmlBody = document.createElement('div');
        htmlBody.classList.add('dialog-htmlBody');
        htmlBody.innerHTML = dialogConfig.htmlBody;
        addCustomClasses(htmlBody, dialogConfig.customClasses?.htmlBody);
        dialog.appendChild(htmlBody);
    }

    // Add buttons in order
    // If it's a destructive dialog, then a red button should appear on the left.
    // If it's non descructive dialog, then a blue button should appear on the right.
    addButtons(dialog, dialogConfig.btnConfigs, dialogConfig.centerButtons);

    // Attach and show dialog
    document.body.appendChild(dialog);
    document.body.classList.add('dialog-open');

    if (dialogConfig.onDialogOpen) dialogConfig.onDialogOpen();
    dialog.showModal();

    // Set focus on buttons by default, needs to be turned off
    if (dialogConfig.focusButtons !== false) setButtonFocus(dialog);

    return dialog;
}

function setButtonFocus(dialog: HTMLDialogElement) {
    const primary = dialog.getElementsByClassName('btn-primary')[0];
    if (primary) {
        (primary as HTMLButtonElement).focus();
        return;
    }

    const danger = dialog.getElementsByClassName('btn-danger')[0];
    if (danger) {
        (danger as HTMLButtonElement).focus();
        return;
    }

    const secondary = dialog.getElementsByClassName('btn-secondary')[0];
    if (secondary) (secondary as HTMLButtonElement).focus();
}

function addButtons(
    dialog: HTMLDialogElement,
    btnConfigs: ButtonConfig[],
    centerBtns: boolean | undefined,
) {
    if (btnConfigs?.length === 0) return;

    const buttonContainer = document.createElement('div');

    if (!centerBtns) buttonContainer.classList.add('button-container', 'ms-auto', 'me-3', 'pt-3', 'd-flex');
    else buttonContainer.classList.add('button-container', 'pt-3', 'd-flex', 'justify-content-center');

    btnConfigs
        // If there's a condition, and that condition is false return.
        .filter((btnConfig) => !btnConfig.condition || btnConfig.condition())
        .forEach((btnConfig, index: number) => {

            const btn = document.createElement('button');
            btn.innerHTML = btnConfig.text;
            btn.classList.add('btn');

            // Initial button should be pushed to the right
            if (index === 0 && !centerBtns) btn.classList.add('ms-auto');
            if (index !== btnConfigs.length - 1) btn.classList.add('me-2');

            // Change colour of button based on purpose
            if (btnConfig.colour === 'Primary') btn.classList.add('btn-primary');
            else if (btnConfig.colour === 'Danger') btn.classList.add('btn-danger');
            else btn.classList.add('btn-secondary');

            // By default, buttons should close the dialog.
            // However, with timeout, we want the dialog (and its backdrop) to stay until page reloads.
            const closeDialog = btnConfig.closeDialog === undefined ? true : btnConfig.closeDialog;

            // Add any event listeners to the buttons
            if (btnConfig.onClickEvent) {
                btn.addEventListener('click', (e) => {
                    e.preventDefault();
                    if (!isFormValid(dialog, btnConfig.preClickValidation)) return;
                    if (!btnConfig.preClickEvent || btnConfig.preClickEvent()) {
                        if (closeDialog) dialogClose(dialog);
                        btnConfig.onClickEvent?.(e);
                    }
                });
            } else {
                btn.addEventListener('click', (e) => {
                    e.preventDefault();
                    if (closeDialog) dialogClose(dialog);
                });
            }

            buttonContainer.appendChild(btn);
        });

    dialog.appendChild(buttonContainer);
}

function dialogClose(dialog: HTMLDialogElement): void {
    dialog.close();
    document.body.classList.remove('dialog-open');
}

function isFormValid(
    dialog: HTMLDialogElement,
    preClickVal: PreClickValidation | PreClickValidation[] | undefined,
) {
    if (!preClickVal) return true;
    const arrayOfValidations = Array.isArray(preClickVal) ? preClickVal : [preClickVal];

    let validInput = true;
    arrayOfValidations.forEach((cond) => {
        const field = dialog.querySelector(`#${cond.fieldId}`) as HTMLInputElement;
        if (!cond.predicate(field)) {
            validInput = false;
            addErrorMessage(field, cond);
        }
    });
    return validInput;
}

function addErrorMessage(field: HTMLInputElement, condition: PreClickValidation) {
    const errorMsgId = condition.errorId || `${condition.fieldId}-error-message`;
    if (document.getElementById(`${errorMsgId}`)) return;

    const errorMsg = document.createElement('div');
    errorMsg.id = `${errorMsgId}`;
    errorMsg.classList.add('text-danger', 'ms-2');
    errorMsg.textContent = condition.errorMsg;

    // Accessibility - See https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Attributes/aria-errormessage
    errorMsg.setAttribute('aria-errormessage', field.id);
    field.setAttribute('aria-invalid', 'true');

    field.after(errorMsg);
    field.addEventListener('input', (_e) => {
        if (condition.predicate(field)) {
            errorMsg.classList.add('d-none');
            field.removeAttribute('aria-invalid');
        } else {
            errorMsg.classList.remove('d-none');
            field.setAttribute('aria-invalid', 'true');
        }
    });
}

// Adds the correct icon based on input into config.
// Acceptable inputs include 'warning', 'error', 'success', 'info' and 'question'
// These svgs were taken from: https://icons.getbootstrap.com/?q=circle
function addIcon(dialog: HTMLDialogElement, icon: 'warning' | 'error' | 'success' | 'info' | 'question') {
    const iconContainer = document.createElement('div');
    iconContainer.id = 'iconContainer';
    iconContainer.classList.add('d-flex', 'justify-content-center', 'my-4');

    if (icon === 'success') {
        iconContainer.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="90" height="90" fill="currentColor" class="bi bi-check-circle" viewBox="0 0 16 16">
            <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14m0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16"/>
            <path d="m10.97 4.97-.02.022-3.473 4.425-2.093-2.094a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-1.071-1.05"/>
            </svg>`;
        iconContainer.classList.add('text-success');
    } else if (icon === 'error') {
        iconContainer.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="90" height="90" fill="currentColor" class="bi bi-x-circle" viewBox="0 0 16 16">
            <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14m0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16"/>
            <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708"/>
            </svg>`;
        iconContainer.classList.add('text-danger');
    } else if (icon === 'warning') {
        iconContainer.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="90" height="90" fill="currentColor" class="bi bi-exclamation-circle" viewBox="0 0 16 16">
            <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14m0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16"/>
            <path d="M7.002 11a1 1 0 1 1 2 0 1 1 0 0 1-2 0M7.1 4.995a.905.905 0 1 1 1.8 0l-.35 3.507a.552.552 0 0 1-1.1 0z"/>
            </svg>`;
        iconContainer.classList.add('text-warning');
    } else if (icon === 'info') {
        iconContainer.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="90" height="90" fill="currentColor" class="bi bi-info-circle" viewBox="0 0 16 16">
            <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14m0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16"/>
            <path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0"/>
            </svg>`;
        iconContainer.classList.add('text-info');
    } else if (icon === 'question') {
        iconContainer.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="90" height="90" fill="currentColor" class="bi bi-question-circle" viewBox="0 0 16 16">
            <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14m0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16"/>
            <path d="M5.255 5.786a.237.237 0 0 0 .241.247h.825c.138 0 .248-.113.266-.25.09-.656.54-1.134 1.342-1.134.686 0 1.314.343 1.314 1.168 0 .635-.374.927-.965 1.371-.673.489-1.206 1.06-1.168 1.987l.003.217a.25.25 0 0 0 .25.246h.811a.25.25 0 0 0 .25-.25v-.105c0-.718.273-.927 1.01-1.486.609-.463 1.244-.977 1.244-2.056 0-1.511-1.276-2.241-2.673-2.241-1.267 0-2.655.59-2.75 2.286m1.557 5.763c0 .533.425.927 1.01.927.609 0 1.028-.394 1.028-.927 0-.552-.42-.94-1.029-.94-.584 0-1.009.388-1.009.94"/>
            </svg>`;
        iconContainer.classList.add('text-secondary');
    }

    if (dialog.querySelector('.dialog-title')) dialog.querySelector('.dialog-title')?.after(iconContainer);
    else dialog.prepend(iconContainer);
}

// Seperate into two different functions setMessage & setHtmlBody
function setMessage(dialog: HTMLDialogElement, text: string) {
    if (!text || !dialog) return;

    // if it exists replace text
    if (dialog.querySelector('.dialog-message')) {
        (dialog.querySelector('.dialog-message') as HTMLElement).textContent = text;
        return;
    }

    // Else create new message
    const dialogMessage = document.createElement('span');
    dialogMessage.textContent = text;
    dialogMessage.classList.add('dialog-message', 'd-block');

    // Add under title, or prepend to dialog body
    const dialogTitle = dialog.querySelector('.dialog-title');
    if (dialogTitle) (dialogTitle as HTMLElement).after(dialogMessage);
    else dialog.prepend(dialogMessage);
}

function setHtmlBody(dialog: HTMLDialogElement, text: string) {
    if (!text || !dialog) return;

    // if it exists replace text
    if (dialog.querySelector('.dialog-htmlBody')) {
        (dialog.querySelector('.dialog-htmlBody') as HTMLElement).innerHTML = text;
        return;
    }

    // Else create new message
    const dialogHtml = document.createElement('div');
    dialogHtml.innerHTML = text;
    dialogHtml.classList.add('dialog-htmlBody', 'd-block');

    // Add under message, title, or prepend to dialog body
    const dialogTitle = dialog.querySelector('.dialog-title');
    const dialogMessage = dialog.querySelector('.dialog-message');
    if (dialogMessage) (dialogMessage as HTMLElement).after(dialogHtml);
    else if (dialogTitle) (dialogTitle as HTMLElement).after(dialogHtml);
    else dialog.prepend(dialogHtml);
}

export default {
    showDialog,
    setMessage,
    setHtmlBody,
};
